// CANONICAL COPY (GH #7115). src/constants/controlIndex.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// Control index — builds lookup structures from the TCF canonical library
// and the framework mapping files.
//
// buildControlIndex() returns:
//   byId              : Map<scfId, control>            — O(1) TCF control lookup
//   byExternalId      : Map<externalId, scfId>         — legacy/external ID → TCF ID
//   controlToFrameworks: Map<scfId, frameworkId[]>     — which frameworks reference this control
//   externalRefs      : Map<scfId, {frameworkId, externalId, ref}[]> — all external citations
//   allFlat           : control[]                      — all TCF controls
//
// A single externalId legitimately maps to multiple TCF controls in many
// frameworks (e.g. NIS2-ART21-5 cites five distinct TCF controls). byExternalId
// can only hold one scfId per externalId, so mapping rows may set `primary:
// true` to declare which one wins that singular lookup — every row still
// contributes to controlToFrameworks/externalRefs regardless of the flag. If
// no row in a duplicate group is marked primary, the last one in array order
// wins (matches historical behavior) — see scripts/check-control-mapping-primary.js,
// which fails the build if a duplicate group has zero or more than one primary.

const { SCF_CONTROLS } = require('./scfControls.cjs');
const { FRAMEWORK_ISO27001 } = require('./frameworks/iso27001.cjs');
const { FRAMEWORK_ISO27002 } = require('./frameworks/iso27002.cjs');
const { FRAMEWORK_CIS18 } = require('./frameworks/cis18.cjs');
const { FRAMEWORK_NIST_CSF20 } = require('./frameworks/nistCsf20.cjs');
const { FRAMEWORK_NIS2 } = require('./frameworks/nis2.cjs');
const { FRAMEWORK_GDPR } = require('./frameworks/gdpr.cjs');
const { FRAMEWORK_GDPR_ISAE3000 } = require('./frameworks/gdprIsae3000Processor.cjs');
const { FRAMEWORK_PCI_DSS } = require('./frameworks/pciDss.cjs');
const { FRAMEWORK_ISO22301 } = require('./frameworks/iso22301.cjs');
const { FRAMEWORK_ISO42001 } = require('./frameworks/iso42001.cjs');
const { FRAMEWORK_EU_AI_ACT } = require('./frameworks/euAiAct.cjs');
const { FRAMEWORK_DORA } = require('./frameworks/dora.cjs');
const { FRAMEWORK_SKI_0215 } = require('./frameworks/ski-0215.cjs');
const { FRAMEWORK_CRA } = require('./frameworks/cra.cjs');

const ALL_FRAMEWORK_DEFS = [
  FRAMEWORK_ISO27001,
  FRAMEWORK_ISO27002,
  FRAMEWORK_CIS18,
  FRAMEWORK_NIST_CSF20,
  FRAMEWORK_NIS2,
  FRAMEWORK_GDPR,
  FRAMEWORK_GDPR_ISAE3000,
  FRAMEWORK_PCI_DSS,
  FRAMEWORK_ISO22301,
  FRAMEWORK_ISO42001,
  FRAMEWORK_EU_AI_ACT,
  FRAMEWORK_DORA,
  FRAMEWORK_SKI_0215,
  FRAMEWORK_CRA,
];

// Module-level cache for the full (no filter) index, which is the common call.
// Saves rebuilding ~190 KB of framework mappings on each component mount.
let _fullIndexCache = null;

/**
 * Resolve the single canonical ("primary") mapping row for each externalId in a
 * list of mapping rows. A row flagged `primary: true` wins; when no row in a
 * duplicate group is flagged, the last row in array order wins (historical
 * behavior). This is the one place the primary-selection rule lives — every
 * consumer that needs one scfId per externalId (byExternalId here, plus
 * scfDedup and Controls) uses it so the choice never silently depends on
 * array order. check-control-mapping-primary.js guarantees exactly one primary
 * per duplicate group.
 *
 * @param {Array<{externalId: string, primary?: boolean}>} mappings
 * @returns {Map<string, object>} externalId → chosen mapping row
 */
function primaryMappingsByExternalId(mappings) {
  const result = new Map();
  const locked = new Set(); // externalIds already fixed by a `primary: true` row
  for (const m of mappings || []) {
    if (m.primary) {
      result.set(m.externalId, m);
      locked.add(m.externalId);
    } else if (!locked.has(m.externalId)) {
      result.set(m.externalId, m);
    }
  }
  return result;
}

function buildControlIndex(activeFrameworkIds) {
  // Return the cached full index when no filter is requested (the common case).
  // Filtered calls (e.g. FrameworkMapView with a specific set) bypass the cache.
  if (!activeFrameworkIds && _fullIndexCache) return _fullIndexCache;

  const byId = new Map(SCF_CONTROLS.map((c) => [c.id, c]));
  const byExternalId = new Map();
  const controlToFrameworks = new Map();
  const externalRefs = new Map();

  const activeSet = activeFrameworkIds
    ? new Set(activeFrameworkIds)
    : new Set(ALL_FRAMEWORK_DEFS.map((f) => f.id));

  // byExternalId holds one scfId per externalId, resolved primary-aware so the
  // singular lookup never depends on mapping array order. externalIds are
  // framework-prefixed and unique per framework, so per-framework resolution is
  // global.
  for (const fw of ALL_FRAMEWORK_DEFS) {
    for (const [externalId, m] of primaryMappingsByExternalId(fw.mappings)) {
      byExternalId.set(externalId, m.scfId);
    }
  }

  for (const fw of ALL_FRAMEWORK_DEFS) {
    if (!activeSet.has(fw.id)) continue;
    for (const m of fw.mappings) {
      if (!controlToFrameworks.has(m.scfId)) controlToFrameworks.set(m.scfId, []);
      if (!controlToFrameworks.get(m.scfId).includes(fw.id)) {
        controlToFrameworks.get(m.scfId).push(fw.id);
      }

      if (!externalRefs.has(m.scfId)) externalRefs.set(m.scfId, []);
      externalRefs.get(m.scfId).push({ frameworkId: fw.id, externalId: m.externalId, ref: m.ref });
    }
  }

  // Build singular map: scfId → first frameworkId (for components that need a single framework per control)
  const controlToFramework = new Map();
  for (const [scfId, fwIds] of controlToFrameworks) {
    if (fwIds.length > 0) controlToFramework.set(scfId, fwIds[0]);
  }

  const result = {
    byId,
    byExternalId,
    controlToFramework,
    controlToFrameworks,
    externalRefs,
    allFlat: SCF_CONTROLS,
  };

  if (!activeFrameworkIds) _fullIndexCache = result;
  return result;
}

module.exports = {
  ALL_FRAMEWORK_DEFS,
  primaryMappingsByExternalId,
  buildControlIndex,
};
